import { selectVisitingCardEditorValue } from '../../selectors/visitingCardEditor/visitingCardEditor';
import { BannerId } from '../../typings/banners';
import {
    EditedBanner,
    getEditedBannerWithCallouts,
    getEditedBannerWithPrice,
    getEditedBannerWithSitelinks
} from '../../typings/bannersEditState';
import { AdvType } from '../../typings/domain';
import { GroupId } from '../../typings/groups';
import { State } from '../../typings/state';
import { getBannerEditPageItemEditorId } from '../BannerHelpers/BannerHelpers';

/**
 * Возвращает счетчик оставшихся символов по лимиту
 * без учета "узких" (бесплатных) символов
 * @param {String} str исходная строка
 * @param {Number} limit лимит символов
 * @param {String} narrowSymbols "узкие" символы
 * @returns {number}
 */
export const strCounterWithoutNarrow = (
    str: string,
    limit: number,
    narrowSymbols: string
): number => {
    const narrowSymbolsRegExp = new RegExp('([' + narrowSymbols + '])', 'g'),
        strWithoutNarrow = (str || '').replace(narrowSymbolsRegExp, '');

    return limit - strWithoutNarrow.length;
};

/**
 * Возвращает счетчик оставшихся символов по лимиту для internal рекламы
 * без учета <br /> и &nbsp; символа
 * @param {String} str исходная строка
 * @returns {number}
 */
export const strLengthForInternal = (str: string): number => {
    return str
        .trim()
        .replace(/<br\s*?\/*?>/g, '')
        .replace(/&nbsp;/g, '')
        .trim().length;
};

/**
 * Возвращает строку убирая знак решетки.
 * @param {String} str исходная строка
 * @returns {string} строка без знаков решетки
 */
export const skipSharpSign = (str: string): string => {
    return (str || '').replace(/\#([^\#]*?)\#/g, '$1');
};

export const maxWordLength = (str: string, limit: number): boolean => {
    // tslint:disable-next-line
    return !new RegExp(`[а-яА-ЯёЁA-Za-z0-9_]{${limit},}`).test(str);
};

export const maxNarrowSymbols = (
    str: string,
    limit: number,
    narrowSymbols: string
): boolean => {
    const narrowSymbolsRegExp = new RegExp('([' + narrowSymbols + '])', 'g'),
        title = skipSharpSign(str),
        narrowsLength = ((title || '').match(narrowSymbolsRegExp) || '').length;

    return narrowsLength <= limit;
};

export const noInvalidSymbols = (
    str: string,
    allowLetters: string
): boolean => {
    return !new RegExp(
        '[^' +
            allowLetters
                .replace(/\\/g, '')
                .replace(/[.?*+^$[\]\\(){}|-]/g, '\\$&') +
            '\\\\]'
    ).test(str);
};

export type NotReadyBanner = {
    id: BannerId;
    groupId: GroupId;
    isNew: boolean;
    issues: BannerIssue[];
};

export enum BannerIssue {
    SITELINKS_OPENED = 'sitelins-opened',
    CALLOUTS_OPENED = 'callouts-opened',
    VCARD_ERRORS = 'vcard-errors',
    BANNER_PRICE_OPENED = 'bannerPrice-opened',
    ERRORS_SITELINKS = 'errors-sitelinks',
    ERRORS_HREF = 'errors-href'
}

export const getBannersNotReadyToSave = (
    state: State,
    banners: Record<BannerId, EditedBanner>
): NotReadyBanner[] => {
    return Object.keys(banners).reduce((result: NotReadyBanner[], bannerId) => {
        const banner = banners[bannerId];
        const editorId = getBannerEditPageItemEditorId(bannerId);
        const currentVisitCard = selectVisitingCardEditorValue(state, editorId);

        // смарт баннеры всегда готовы для сохранения т.к. там нет изменяемых данных
        if (banner.bannerType === AdvType.PERFORMANCE) {
            return result;
        }

        const siteLinksBanner = getEditedBannerWithSitelinks(banner);
        const calloutsBanner = getEditedBannerWithCallouts(banner);
        const priceBanner = getEditedBannerWithPrice(banner);
        const errors = banner.errors || {};
        const siteLinksErrors = errors.sitelinks;
        const hrefErrors = errors.href;
        const flags = {
            [BannerIssue.SITELINKS_OPENED]:
                siteLinksBanner && siteLinksBanner.sitelinks.isOpen,
            [BannerIssue.CALLOUTS_OPENED]:
                calloutsBanner && calloutsBanner.callouts.isEditorOpen,
            [BannerIssue.VCARD_ERRORS]:
                currentVisitCard &&
                currentVisitCard.errors &&
                currentVisitCard.errors.length > 0,
            [BannerIssue.ERRORS_SITELINKS]:
                siteLinksErrors &&
                siteLinksErrors.errors &&
                siteLinksErrors.errors.length > 0,
            [BannerIssue.ERRORS_HREF]:
                hrefErrors && hrefErrors.errors && hrefErrors.errors.length > 0,
            [BannerIssue.BANNER_PRICE_OPENED]:
                priceBanner && priceBanner.bannerPriceEditor.isOpen
        };
        const issues: BannerIssue[] = (Object.keys(
            flags
        ) as BannerIssue[]).filter((flag: BannerIssue) => flags[flag]);

        if (issues.length > 0) {
            result.push({
                id: bannerId,
                groupId: banner.group.id,
                isNew: banner.isNew,
                issues: issues
            });
        }

        return result;
    }, []);
};
